var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import OptionAnalyzerPanelTemplate from '@front/templates/OptionAnalyzerPanelTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { PanelNames } from '../../UtilsClasses/FactoryConstants';
import { ColorStyleWidth, DynProperty } from '@shared/commons/DynProperty';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { ThemeManager } from '../../misc/ThemeManager';
import { ApplicationPanelWithTable } from '../ApplicationPanelWithTable';
import { AnalyzerChartType } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionAnalyzer/AnalyzerChartType';
import { AnalyzerLinesType } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionAnalyzer/AnalyzerLinesType';
import { AnalyzerProbabilityCalculationMode } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionAnalyzer/AnalyzerProbabilityCalculationMode';
import { OptionAnalyzerLineTableItem } from '../../cache/OptionMaster/OptionAnalyzerLineTableItem';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { OptionCalculatorScreen } from '../../screen/OptionCalculatorScreen';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class OptionAnalyzerPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this._isInitialized = false;
        this._isRepopulateChart = false;
        this._isUpdateChart = false;
        this._isResetChartScales = false;
        this.onQuoteChanged = () => {
            this._isUpdateChart = true;
        };
        this.onUpdatePaperPosition = (paperPosition) => {
            this._isRepopulateChart = true;
        };
        this.Name = 'OptionAnalyzerPanel';
        this.headerLocaleKey = 'panel.optionAnalyzer';
        this.NeedCalculateRowCount = false;
    }
    // #region Overrides
    oncomplete() {
        super.oncomplete();
        this.on('expandCollapsSettings_btnClick', this.onExpandCollapseButtonClicked);
        // this.on('refreshProbability_btnClick', this.onRefreshProbabilityButtonClicked);
        this.on('calculator_btnClick', this.onCalculatorButtonClicked);
        this.observe('visible', this.onVisibleChanged);
        this.observe('selectedChartTypeItem', this.onSelectedChartTypeItemChanged);
        this.observe('selectedLinesTypeItem', this.selectedLinesTypeItemChanged);
        this.observe('volatilityChange', this.onVolatilityChangeChanged);
        // this.observe('probabilityCalculationDays', this.onProbabilityCalculationDaysChanged);
        // this.observe('selectedProbabilityCalculationModeItem', this.onSelectedProbabilityCalculationModeItemChanged);
    }
    onteardown() {
        const optionTrader = this._optionTrader;
        if (!isNullOrUndefined(optionTrader)) {
            this._optionTrader.unsubscribeOnQuoteChanged(this.onQuoteChanged);
            this._optionTrader.unsubscribeOnCreatePaperPosition(this.onUpdatePaperPosition);
            this._optionTrader.unsubscribeOnRemovePaperPosition(this.onUpdatePaperPosition);
            this._optionTrader.unsubscribeOnUpdatePaperPosition(this.onUpdatePaperPosition);
        }
        if (!isNullOrUndefined(this._quickTable)) {
            this._quickTable.AfterEditItem.UnSubscribe(this.onAfterEditItem, this);
        }
        this.off('expandCollapsSettings_btnClick', this.onExpandCollapseButtonClicked);
        // this.off('refreshProbability_btnClick', this.onRefreshProbabilityButtonClicked);
        this.off('calculator_btnClick', this.onCalculatorButtonClicked);
        super.onteardown();
    }
    getType() {
        return PanelNames.OptionAnalyzerPanel;
    }
    jbInit() {
        const quickTableRactive = super.findAllComponents('quickTableRactive')[0];
        const chartRactive = super.findAllComponents('optionAnalyzerChartRactive')[0];
        this._quickTable = quickTableRactive.quickTable;
        this._chart = chartRactive.terceraChart;
        super.jbInit();
        this._quickTable.showHeader = false;
        this._quickTable.InitializeDirect(new OptionAnalyzerLineTableItem(null, null, null));
        this._quickTable.UpdateSortedColumns();
        this._quickTable.AfterEditItem.Subscribe(this.onAfterEditItem, this);
    }
    localize() {
        super.localize();
        this.localizeVolatilityLabels();
        void this.set({
            settingsLabel: Resources.getResource('general.Settings'),
            chartTypeLabel: Resources.getResource('panel.optionAnalyzer.chartTypeLabel'),
            linesLabel: Resources.getResource('panel.optionAnalyzer.linesLabel'),
            probabilityCalculationLabel: Resources.getResource('property.ProbabilityCalculation'),
            daysLabel: Resources.getResource('panel.optionAnalyzer.daysLabel'),
            modeLabel: Resources.getResource('panel.optionAnalyzer.modeLabel'),
            buttonCalculatorText: Resources.getResource('property.Calculator')
        });
        this._quickTable.localize();
        this._chart.Localize();
        this.update();
    }
    callThemeChange() {
        this.themeChange();
    }
    callCallback(properties) {
        this.callBack(properties);
    }
    themeChange() {
        super.themeChange();
        if (isNullOrUndefined(this._optionTrader)) {
            return;
        }
        const chartRactive = this.findComponent('optionAnalyzerChartRactive');
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        const currentTheme = ThemeManager.colors;
        optionAnalyzer.intrinsicColor = currentTheme.OptionMasterAnalyzerIntrinsicColor;
        optionAnalyzer.timeLineColor = currentTheme.OptionMasterAnalyzerTimeLineColor;
        optionAnalyzer.basePriceLineColor = currentTheme.OptionMasterAnalyzerZeroLineColor;
        optionAnalyzer.zeroLineColor = currentTheme.OptionMasterAnalyzerZeroLineColor;
        optionAnalyzer.underlierPriceColor = currentTheme.OptionMasterAnalyzerUnderlierPriceColor;
        this.themeChangeLines();
        const quickTableRactive = this.findComponent('quickTableRactive');
        quickTableRactive.themeChange();
        chartRactive.themeChange();
    }
    Properties() {
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        const properties = super.Properties();
        let index = 0;
        const separatorGroup = '#0#' + Resources.getResource('property.appearance');
        let dp = new DynProperty('property.Intrinsic', new ColorStyleWidth(optionAnalyzer.intrinsicColor, optionAnalyzer.intrinsicStyle, optionAnalyzer.intrinsicWidth), DynProperty.COLOR_STYLE_WIDTH, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        dp = new DynProperty('property.TimeLine', new ColorStyleWidth(optionAnalyzer.timeLineColor, optionAnalyzer.timeLineStyle, optionAnalyzer.timeLineWidth), DynProperty.COLOR_STYLE_WIDTH, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        dp = new DynProperty('property.ZeroLine', new ColorStyleWidth(optionAnalyzer.zeroLineColor, optionAnalyzer.zeroLineStyle, optionAnalyzer.zeroLineWidth), DynProperty.COLOR_STYLE_WIDTH, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        dp = new DynProperty('property.UnderlierPrice', new ColorStyleWidth(optionAnalyzer.underlierPriceColor, optionAnalyzer.underlierPriceStyle, optionAnalyzer.underlierPriceWidth), DynProperty.COLOR_STYLE_WIDTH, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        /* separatorGroup = '#1#' + Resources.getResource('property.ProbabilitySimulations');
        dp = new DynProperty('property.SimulationModel', optionAnalyzer.simulationModel, DynProperty.COMBOBOX, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        dp.objectVariants = [
            { text: Resources.getResource('property.SimulationModel.ByAbsolutePrices'), value: SimulationModel.ByAbsolutePrices },
            { text: Resources.getResource('property.SimulationModel.ByRelativePrices'), value: SimulationModel.ByRelativePrices },
            { text: Resources.getResource('property.SimulationModel.ByLogarithmicPrices'), value: SimulationModel.ByLogarithmicPrices }
        ];
        properties.push(dp);
        dp = new DynProperty('property.SimulationExamples', optionAnalyzer.simulationExamples, DynProperty.INTEGER, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.minimalValue = 100;
        dp.maximalValue = NumericUtils.MAXVALUE;
        dp.increment = 1;
        dp.decimalPlaces = 0;
        properties.push(dp);
        dp = new DynProperty('property.ProbabilityScenario', optionAnalyzer.probabilityScenario, DynProperty.COMBOBOX, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        dp.objectVariants = [
            { text: Resources.getResource('property.ProbabilityScenario.OneTouch'), value: ProbabilityScenario.OneTouch },
            { text: Resources.getResource('property.ProbabilityScenario.OutOfRange'), value: ProbabilityScenario.OutOfRange }
        ];
        properties.push(dp);
        dp = new DynProperty('property.HistoryPeriodYear', optionAnalyzer.historyPeriodYear, DynProperty.INTEGER, DynProperty.ANALYZER_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.minimalValue = 1;
        dp.maximalValue = NumericUtils.MAXVALUE;
        dp.increment = 1;
        dp.decimalPlaces = 0;
        properties.push(dp); */
        dp = new DynProperty('property.ChartType', optionAnalyzer.chartType, DynProperty.COMBOBOX, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = index++;
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(dp);
        dp = new DynProperty('property.Lines', optionAnalyzer.linesType, DynProperty.COMBOBOX, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = index++;
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(dp);
        dp = new DynProperty('property.VolatilityChange', optionAnalyzer.volatilityChange, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = index++;
        properties.push(dp);
        dp = new DynProperty('property.Volatility', JSON.stringify(optionAnalyzer.volatilityLines), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = index++;
        properties.push(dp);
        /*         dp = new DynProperty('property.Days', optionAnalyzer.probabilityCalculationDays, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = index++;
        properties.push(dp);

        dp = new DynProperty('property.ProbabilityCalculation', optionAnalyzer.probabilityCalculationMode, DynProperty.COMBOBOX, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = index++;
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(dp); */
        properties.push(...this._chart.Properties());
        return properties;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        let dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.Intrinsic');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const colorStyleWidth = dp.value;
            optionAnalyzer.intrinsicColor = colorStyleWidth.Color;
            optionAnalyzer.intrinsicStyle = colorStyleWidth.Style;
            optionAnalyzer.intrinsicWidth = colorStyleWidth.Width;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.TimeLine');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const colorStyleWidth = dp.value;
            optionAnalyzer.timeLineColor = colorStyleWidth.Color;
            optionAnalyzer.timeLineStyle = colorStyleWidth.Style;
            optionAnalyzer.timeLineWidth = colorStyleWidth.Width;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.ZeroLine');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const colorStyleWidth = dp.value;
            optionAnalyzer.zeroLineColor = colorStyleWidth.Color;
            optionAnalyzer.zeroLineStyle = colorStyleWidth.Style;
            optionAnalyzer.zeroLineWidth = colorStyleWidth.Width;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.UnderlierPrice');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const colorStyleWidth = dp.value;
            optionAnalyzer.underlierPriceColor = colorStyleWidth.Color;
            optionAnalyzer.underlierPriceStyle = colorStyleWidth.Style;
            optionAnalyzer.underlierPriceWidth = colorStyleWidth.Width;
        }
        /*         dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.SimulationModel');
        if (!isNullOrUndefined(dp?.value)) {
            optionAnalyzer.simulationModel = dp.value as SimulationModel;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.SimulationExamples');
        if (!isNullOrUndefined(dp?.value)) {
            optionAnalyzer.simulationExamples = dp.value as number;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.ProbabilityScenario');
        if (!isNullOrUndefined(dp?.value)) {
            optionAnalyzer.probabilityScenario = dp.value as ProbabilityScenario;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.ANALYZER_GROUP, 'property.HistoryPeriodYear');
        if (!isNullOrUndefined(dp?.value)) {
            optionAnalyzer.historyPeriodYear = dp.value as number;
        } */
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.HIDDEN_GROUP, 'property.ChartType');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            optionAnalyzer.chartType = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.HIDDEN_GROUP, 'property.Lines');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            optionAnalyzer.linesType = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.HIDDEN_GROUP, 'property.VolatilityChange');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            optionAnalyzer.volatilityChange = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.HIDDEN_GROUP, 'property.Volatility');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const analyzerVolatilityLines = JSON.parse(dp.value);
            if (analyzerVolatilityLines.length === optionAnalyzer.volatilityLines.length) {
                for (let i = 0; i < analyzerVolatilityLines.length; i++) {
                    optionAnalyzer.volatilityLines[i].isActive = analyzerVolatilityLines[i].isActive;
                    optionAnalyzer.volatilityLines[i].value = analyzerVolatilityLines[i].value;
                    optionAnalyzer.volatilityLines[i].width = analyzerVolatilityLines[i].width;
                    optionAnalyzer.volatilityLines[i].color = analyzerVolatilityLines[i].color;
                }
            }
        }
        /*         dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.HIDDEN_GROUP, 'property.Days');
        if (!isNullOrUndefined(dp?.value)) {
            optionAnalyzer.probabilityCalculationDays = dp.value as number;
        }
        dp = DynProperty.getPropertyByGroupAndName(newProperties, DynProperty.HIDDEN_GROUP, 'property.ProbabilityCalculation');
        if (!isNullOrUndefined(dp?.value)) {
            optionAnalyzer.probabilityCalculationMode = dp.value as AnalyzerProbabilityCalculationMode;
        } */
        this._chart.callBack(newProperties);
        this.repopulate();
    }
    TickAsync() {
        super.TickAsync();
        const isVisible = this.get('visible');
        if (!isVisible) {
            return;
        }
        if (this._isRepopulateChart) {
            this._isRepopulateChart = false;
            this.repopulateChart();
        }
        if (this._isResetChartScales) {
            this._isResetChartScales = false;
            this._chart.resetScales();
        }
        if (this._chart.needRedraw) {
            this._chart.needRedraw = false;
            this._chart.Draw();
            this._chart.AddForceUpdate();
        }
        this._quickTable.updateRowsCellsValues();
        this._quickTable.Draw();
        this._quickTable.needRedraw = false;
        this._quickTable.needRedrawBackground = true;
    }
    layoutTable() {
        super.layoutTable();
        this.layoutTableResize(this.findComponent('optionAnalyzerChartRactive'));
        for (const table of this.findAllComponents('quickTableRactive')) {
            this.layoutTableResize(table);
        }
    }
    repopulate() {
        super.repopulate();
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        const chartTypeItems = this.get('chartTypeItems');
        const linesTypeItems = this.get('linesTypeItems');
        const probabliltyCalculationModeItems = this.get('probabilityCalculationModeItems');
        void this.set({
            selectedChartTypeItem: chartTypeItems.find((x) => x.value === optionAnalyzer.chartType),
            selectedLinesTypeItem: linesTypeItems.find((x) => x.value === optionAnalyzer.linesType),
            volatilityChange: optionAnalyzer.volatilityChange,
            volatilityLines: optionAnalyzer.volatilityLines,
            probabilityCalculationDays: optionAnalyzer.probabilityCalculationDays,
            selectedProbabilityCalculationModeItem: probabliltyCalculationModeItems.find((x) => x.value === optionAnalyzer.probabilityCalculationMode)
        });
        this.localizeVolatilityLabels();
        this.repopulateTable();
    }
    updateChart() {
        if (this._isUpdateChart) {
            this._isUpdateChart = false;
            this._chart.update(this._optionTrader.getOptionAnalyzerChartData());
        }
    }
    // #endregion
    // #region Eventhandlers
    onVisibleChanged() {
        this.layoutTable();
        this._isResetChartScales = true;
    }
    onExpandCollapseButtonClicked() {
        return __awaiter(this, void 0, void 0, function* () {
            const isExpanded = this.get('isSettingsExpanded');
            yield this.set('isSettingsExpanded', !isExpanded);
            this.layoutTable();
        });
    }
    /*     private onRefreshProbabilityButtonClicked (): void {
        this._chartRactive.refreshProbabilityValues();
    } */
    onCalculatorButtonClicked() {
        OptionCalculatorScreen.Show(this._optionTrader.getCalculator());
    }
    onSelectedChartTypeItemChanged() {
        if (!this._isInitialized) {
            return;
        }
        const selectedChartTypeItem = this.get('selectedChartTypeItem');
        this._optionTrader.optionAnalyzer.chartType = selectedChartTypeItem.value;
        this._isRepopulateChart = true;
        this._isResetChartScales = true;
    }
    selectedLinesTypeItemChanged(value, oldValue) {
        if (!this._isInitialized) {
            return;
        }
        if (value === oldValue) {
            return;
        }
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        const selectedLinesTypeItem = this.get('selectedLinesTypeItem');
        const linesType = selectedLinesTypeItem.value;
        optionAnalyzer.linesType = linesType;
        optionAnalyzer.resetVolatityLinesToDefault();
        void this.set({
            volatilityChangeMinValue: linesType === AnalyzerLinesType.VolatilityChange ? 0 : NumericUtils.MINVALUE,
            volatilityChangeStep: linesType === AnalyzerLinesType.VolatilityChange ? 1 : 0.01,
            volatilityChangeDecimalPlaces: linesType === AnalyzerLinesType.VolatilityChange ? 0 : 2
        });
        this.localizeVolatilityLabels();
        this.themeChangeLines();
        this.repopulateTable();
        this._isRepopulateChart = true;
        this._isResetChartScales = true;
    }
    onVolatilityChangeChanged() {
        if (!this._isInitialized) {
            return;
        }
        const volatilityChange = this.get('volatilityChange');
        this._optionTrader.optionAnalyzer.volatilityChange = volatilityChange;
        this._isUpdateChart = true;
    }
    /* private onProbabilityCalculationDaysChanged (): void {
        if (!this._isInitialized) {
            return;
        }
        const probabilityCalculationDays = this.get('probabilityCalculationDays');
        this._optionTrader.optionAnalyzer.probabilityCalculationDays = probabilityCalculationDays;
    }

         private onSelectedProbabilityCalculationModeItemChanged (): void {
        if (!this._isInitialized) {
            return;
        }
        const selectedProbabilityCalculationModeItem = this.get('selectedProbabilityCalculationModeItem');
        this._optionTrader.optionAnalyzer.probabilityCalculationMode = selectedProbabilityCalculationModeItem.value;
        this._chartRactive.refreshProbabilityLines();
    } */
    onAfterEditItem(data) {
        const tableItem = data.row.item;
        switch (data.realColumnIndex) {
            case OptionAnalyzerLineTableItem.ACTIVE_COL_INDEX:
                tableItem.analyzerLine.isActive = !tableItem.analyzerLine.isActive;
                this._isRepopulateChart = true;
                break;
            case OptionAnalyzerLineTableItem.VALUE_COL_INDEX:
                tableItem.analyzerLine.value = data.newValue;
                this._isUpdateChart = true;
                break;
            case OptionAnalyzerLineTableItem.WIDTH_COL_INDEX:
                tableItem.analyzerLine.width = data.newValue;
                this._isUpdateChart = true;
                break;
            case OptionAnalyzerLineTableItem.COLOR_COL_INDEX:
                tableItem.analyzerLine.color = data.newValue;
                this._isUpdateChart = true;
                break;
        }
    }
    // #endregion
    setOptionTrader(optionTrader) {
        this._optionTrader = optionTrader;
        this._optionTrader.subscribeOnQuoteChanged(this.onQuoteChanged);
        this._optionTrader.subscribeOnCreatePaperPosition(this.onUpdatePaperPosition);
        this._optionTrader.subscribeOnRemovePaperPosition(this.onUpdatePaperPosition);
        this._optionTrader.subscribeOnUpdatePaperPosition(this.onUpdatePaperPosition);
        this._chart.attachOptionTrader(optionTrader);
        this._isInitialized = true;
    }
    applyCursor(cursor) {
        const chartRactive = this.findComponent('optionAnalyzerChartRactive');
        if (!isNullOrUndefined(chartRactive)) {
            void chartRactive.setCursor(cursor);
        }
    }
    localizeVolatilityLabels() {
        if (!this._isInitialized) {
            return;
        }
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        const isVolatilityChange = optionAnalyzer.linesType === AnalyzerLinesType.VolatilityChange;
        void this.set({
            volatilityNumericLabel: isVolatilityChange
                ? Resources.getResource('panel.optionAnalyzer.tPlusLabel')
                : Resources.getResource('panel.optionAnalyzer.volatityChangeLabel'),
            volatilityTableLabel: isVolatilityChange
                ? Resources.getResource('panel.optionAnalyzer.volatityLabel')
                : Resources.getResource('panel.optionAnalyzer.tPlusDaysLabel')
        });
    }
    themeChangeLines() {
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        for (let i = 0; i < optionAnalyzer.volatilityLines.length; i++) {
            const volatilityLine = optionAnalyzer.volatilityLines[i];
            if (!isValidString(volatilityLine.color)) {
                optionAnalyzer.volatilityLines[i].color = ThemeManager.colors.OptionMasterAnalyzerVolatilityLineColor;
            }
        }
    }
    repopulateTable() {
        const optionAnalyzer = this._optionTrader.optionAnalyzer;
        const volatilityLines = optionAnalyzer.volatilityLines;
        this._quickTable.ClearAll();
        for (let i = 0; i < volatilityLines.length; i++) {
            const volatilityLine = volatilityLines[i];
            this._quickTable.AddItem(new OptionAnalyzerLineTableItem(volatilityLine, optionAnalyzer.linesType, SessionSettings));
        }
    }
    repopulateChart() {
        this._chart.set(this._optionTrader.getOptionAnalyzerChartData());
    }
}
ApplicationPanelWithTable.extendWith(OptionAnalyzerPanel, {
    template: OptionAnalyzerPanelTemplate,
    data: function () {
        return {
            settingsLabel: '',
            chartTypeLabel: '',
            linesLabel: '',
            volatilityNumericLabel: '',
            volatilityTableLabel: '',
            probabilityCalculationLabel: '',
            daysLabel: '',
            modeLabel: '',
            buttonCalculatorText: '',
            isSettingsExpanded: true,
            chartTypeItems: [
                { type: NewContextMenuItemType.Default, text: 'property.PNL', value: AnalyzerChartType.PL },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.optionChain.delta',
                    value: AnalyzerChartType.Delta
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.optionChain.gamma',
                    value: AnalyzerChartType.Gamma
                },
                { type: NewContextMenuItemType.Default, text: 'panel.optionChain.vega', value: AnalyzerChartType.Vega },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.optionChain.theta',
                    value: AnalyzerChartType.Theta
                },
                { type: NewContextMenuItemType.Default, text: 'panel.optionChain.rho', value: AnalyzerChartType.Rho }
            ],
            selectedChartTypeItem: undefined,
            linesTypeItems: [
                {
                    type: NewContextMenuItemType.Default,
                    text: 'property.VolatilityChange',
                    value: AnalyzerLinesType.VolatilityChange
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'property.TimeChange',
                    value: AnalyzerLinesType.TimeChange
                }
            ],
            selectedLinesTypeItem: undefined,
            volatilityChangeMinValue: 0,
            volatilityChangeMaxValue: NumericUtils.MAXVALUE,
            volatilityChangeStep: 1,
            volatilityChangeDecimalPlaces: 0,
            volatilityChange: 0,
            volatilityLines: [],
            probabilityCalculationDaysMinValue: 1,
            probabilityCalculationDaysMaxValue: 99999,
            probabilityCalculationDaysStep: 1,
            probabilityCalculationDays: 10,
            probabilityCalculationModeItems: [
                {
                    type: NewContextMenuItemType.Default,
                    text: 'property.ProbabilityCalculation.None',
                    value: AnalyzerProbabilityCalculationMode.None
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'property.ProbabilityCalculation.Single',
                    value: AnalyzerProbabilityCalculationMode.Single
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'property.ProbabilityCalculation.OR',
                    value: AnalyzerProbabilityCalculationMode.OR
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'property.ProbabilityCalculation.AND',
                    value: AnalyzerProbabilityCalculationMode.AND
                }
            ],
            selectedProbabilityCalculationModeItem: undefined
        };
    },
    computed: {
        terceraChartPanelContext: {
            get: function () {
                return this;
            },
            set: function (value) { }
        }
    }
});
